import { hasValue, log, processImageFileToDataUrl, styles } from "@repo/helpers";
import { ChangeEvent, memo, useEffect, useRef, useState } from "react";
import { BrowserView, MobileView } from "react-device-detect";

import { Button, ButtonSize, ButtonTypes, Spinner } from "../../index";
import { Camera, CameraProps, FACING_MODES } from "../camera";
import Col from "../col";
import { InputComponent } from "../input";
import Row from "../row";
import style from "./style.module.scss";
import { Props, SerializedFile } from "./types";

export function TakePicture(props: Props) {
	const {
		openAtStart,
		review,
		type = "jpg",
		isSelfie,
		height,
		width,
		sizeFactor = 1,
		instructionText,
		fullWidth,
		showLoading,
		isMobile = false,
		mobileOnly = false,
		...inputProps
	} = props;
	const { value, label, id } = inputProps;
	const { changeHandler, onError = () => {} } = props;

	const [photo, setPhoto] = useState<string>();
	const [takingPhoto, setTakingPhoto] = useState<boolean>();
	const [reviewing, setReviewing] = useState<boolean>();
	const [showCancel, setShowCancel] = useState<boolean>();
	const [loading, setLoading] = useState<boolean>(showLoading ?? false);

	const fileInputRef = useRef<HTMLInputElement>();

	const savedPhoto = value
		? value.url?.startsWith("data:")
			? value.url
			: `data:${value.type};base64,${value.url}`
		: undefined;

	useEffect(() => {
		if (openAtStart) {
			setTakingPhoto(true);
			setShowCancel(false);
		}
	}, [openAtStart]);

	useEffect(() => {
		if (value) {
			setPhoto(savedPhoto);
			setReviewing(false);
		}
	}, [savedPhoto, value]);

	function takePhotoClicked() {
		setTakingPhoto(true);
		setShowCancel(!openAtStart);
		setPhoto(null);
		if (isMobile || mobileOnly) openMobileCamera();
	}

	function takePhoto(img: string) {
		setPhoto(img);
		setReviewing(review);
		setTakingPhoto(false);

		//If not reviewing, update photo immediately
		if (!review) bUsePhoto(img);
	}

	//if just "usePhotoClicked", react hooks complains
	function bUsePhotoClicked() {
		bUsePhoto(photo);
	}

	function bUsePhoto(img: string) {
		const fileType = type === "jpg" ? "image/jpeg" : "image/png";

		const file = {
			name: label,
			type: fileType,
			url: img,
		} as SerializedFile;
		changeHandler(file, id);
		exitCamera();
	}
	function exitCamera() {
		let newPhoto = photo;
		if (!value) newPhoto = null;
		setReviewing(false);
		setTakingPhoto(false);
		setPhoto(newPhoto);
	}
	function onCameraError(e: Error) {
		log(e.message, "error");
		onError(e?.message);
	}
	function openMobileCamera() {
		fileInputRef.current.click();
	}

	return (
		<InputComponent {...inputProps} hideAddon>
			<div className={styles(fullWidth && style.container)}>{renderInnards()}</div>
			<input type="file" accept="image/*" capture="environment" ref={fileInputRef} onChange={photoTaken} />
		</InputComponent>
	);

	function renderInnards() {
		if (takingPhoto && !value) return renderTakePhoto();
		else if (reviewing && photo) return reviewingPhoto();
		else if (value) return renderCanReTakePhoto();
		return renderCanTakePhoto();
	}

	function renderCanTakePhoto() {
		return (
			<Row className={style.buttonContainer}>
				<Col xs={12}>
					<Button onClick={takePhotoClicked} types={[ButtonTypes.Primary]}>
						Use Camera
					</Button>
				</Col>
			</Row>
		);
	}

	function renderCanReTakePhoto() {
		return (
			<>
				<Row>{renderTakenPhoto(savedPhoto)}</Row>
				<Row className={style.buttonContainer}>
					<Col xs={12}>
						<Button onClick={takePhotoClicked} types={[ButtonTypes.Primary]}>
							{mobileOnly && !isMobile ? "Choose Photo" : "Retake Photo"}
						</Button>
					</Col>
				</Row>
			</>
		);
	}

	function renderTakePhoto() {
		return (
			<>
				<div>{instructionText && <label>{instructionText}</label>}</div>
				{renderPhotoCapture()}
				<Row className={style.buttonContainer}>
					{showCancel && (
						<Col xs={12}>
							<Button onClick={exitCamera} types={[ButtonTypes.Danger]}>
								Cancel
							</Button>
						</Col>
					)}
				</Row>
			</>
		);
	}

	function reviewingPhoto() {
		return (
			<>
				<Row>{renderTakenPhoto()}</Row>
				<div className="grid w-full grid-cols-1 gap-2 pt-2">
					<Button onClick={bUsePhotoClicked} types={[ButtonTypes.Green]}>
						Use Photo
					</Button>
					<Button onClick={takePhotoClicked} types={[ButtonTypes.Primary]}>
						Retake
					</Button>
				</div>
			</>
		);
	}

	async function photoTaken(event: ChangeEvent<HTMLInputElement>) {
		if (event.target.files.length === 0) return;
		const file = event.target.files.item(0);
		try {
			const outputType = type === "jpg" ? "image/jpeg" : "image/png";
			const { dataUrl } = await processImageFileToDataUrl(file, {
				type: outputType,
			});
			takePhoto(dataUrl);
		} catch (ex) {
			log(`Error processing image: ${ex?.message ?? ex}`, "error");
			onError?.("Failed to process image");
		}
	}

	//Renders camera
	function renderPhotoCapture() {
		return (
			<>
				<MobileView>
					<Button onClick={openMobileCamera} types={[ButtonTypes.Primary]} size={ButtonSize.large} fullWidth>
						Take Photo
					</Button>
				</MobileView>
				<BrowserView>
					{mobileOnly ? (
						<Button
							onClick={openMobileCamera}
							types={[ButtonTypes.Primary]}
							size={ButtonSize.large}
							fullWidth
						>
							Upload Photo
						</Button>
					) : (
						<Row className={styles(fullWidth && style.fullWidth)}>
							{loading && <Spinner tag="h1">Loading...</Spinner>}
							<CameraWrapper
								onTakePhoto={takePhoto}
								imageType={type}
								idealResolution={{ width: width, height: height }}
								idealFacingMode={isSelfie ? FACING_MODES.USER : FACING_MODES.ENVIRONMENT}
								sizeFactor={sizeFactor}
								onCameraStart={onCameraStart}
								isDisplayStartCameraError
								onCameraError={onCameraError}
								isSilentMode
								isImageMirror={!isSelfie}
							/>
						</Row>
					)}
				</BrowserView>
			</>
		);
	}

	//Display image, either from source, or state photo
	function renderTakenPhoto(src?: string) {
		return (
			<div className={style.imageContainer}>
				<img
					className={styles("img-rounded", "img-thumbnail", style.image)}
					src={src ?? photo}
					alt="Captured"
				/>
			</div>
		);
	}

	function onCameraStart() {
		setLoading(false);
	}
}

const CameraWrapper = memo(function _(props: CameraProps) {
	//Wrapping to avoid needless re-renders
	return <Camera {...props} />;
}, compareProps);

function compareProps<T>(obj1: T, obj2: T) {
	if (hasValue(obj1) !== hasValue(obj2)) return false;
	if (!obj1) return true;
	for (const key in obj1) {
		const prop1 = obj1[key];
		const prop2 = obj2[key];
		if (prop1 !== prop2) {
			if (typeof prop1 === "object") {
				if (!compareProps(prop1, prop2)) return false;
			}
			//We could make passed functions callbacks, but this was simpler
			else if (typeof prop1 === "function") {
				continue;
			} else return false;
		}
	}
	return true;
}
